import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Calendar, Clock, ArrowLeft, Mail, ShieldCheck } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import { resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';

/**
 * MerciPage — Page de remerciement universelle après soumission d'un formulaire B2B.
 *
 * Branchée à :
 *   - Homepage `/` (formulaire "Prêt à renforcer votre équipe ?")
 *   - Landing Suisse `/recruter-suisse` (formulaire B2B Suisse)
 *
 * Cette page :
 *   1. Confirme la bonne réception du formulaire
 *   2. Informe le visiteur qu'il sera appelé par notre assistante virtuelle
 *      dans le but de fixer un rendez-vous avec le responsable de l'agence
 *   3. Déclenche l'événement Google Ads "conversion" (AW-18129722541/iC8VCJKK9qUcEK259sRD)
 */
const MerciPage = () => {
  const location = useLocation();
  // Métadonnées éventuelles passées par la page d'origine (ex: nom du candidat)
  const fromForm = location.state?.fromForm || 'unknown'; // 'homepage' | 'suisse_landing' | 'unknown'
  const visitorName = (location.state?.name || '').toString().trim();

  // ---- Google Ads conversion fire-once-on-mount ----
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18129722541/iC8VCJKK9qUcEK259sRD',
          value: 1.0,
          currency: 'CHF',
        });
      }
    } catch (_) {
      /* gtag indisponible (adblock) — silencieux */
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-tp-bg" data-testid="merci-page">
      <SeoHead
        pageTitle="Demande reçue — Talent Partner"
        pageDescription="Votre demande est bien arrivée. Notre assistante virtuelle vous appelle sous 24h ouvrées pour fixer un rendez-vous avec le responsable de l'agence."
        canonical="https://talent-partner-solutions.com/merci"
        noindex
      />

      {/* Header minimal */}
      <header className="bg-tp-primary text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={resolveAssetUrl('/branding/logo_horizontal.png')}
              alt="Talent Partner"
              className="h-8 sm:h-9 object-contain"
              onError={(e) => {
                e.currentTarget.outerHTML = '<span class="font-display font-semibold text-white text-lg">Talent Partner</span>';
              }}
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-white/80 hover:text-white inline-flex items-center gap-1"
            data-testid="merci-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Accueil
          </Link>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="flex-1 flex items-start justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-2xl">
          {/* Card principale */}
          <div className="bg-white rounded-2xl shadow-lg border border-tp-border p-6 sm:p-10 text-center" data-testid="merci-card">
            {/* Big check */}
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-tp-accent/15 rounded-full flex items-center justify-center mx-auto mb-5">
              <ShieldCheck className="h-9 w-9 sm:h-11 sm:w-11 text-tp-accent" strokeWidth={2.2} />
            </div>

            {/* Titre */}
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-tp-primary leading-tight">
              {visitorName ? `Merci ${visitorName} !` : 'Demande bien reçue.'}
            </h1>
            <p className="mt-3 text-tp-text leading-relaxed text-sm sm:text-base">
              Votre formulaire est <strong>bien arrivé</strong> chez nous.
              <br />
              <span className="text-tp-text-light">Nous le traitons en priorité.</span>
            </p>

            {/* Bloc action — RESTEZ PRÈS DU TÉLÉPHONE */}
            <div className="mt-7 sm:mt-8 bg-tp-primary/5 border-2 border-tp-primary/20 rounded-xl p-5 sm:p-6 text-left">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-tp-primary text-white flex items-center justify-center">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display font-semibold text-tp-primary text-base sm:text-lg leading-snug">
                    Restez près de votre téléphone
                  </h2>
                  <p className="mt-1.5 text-sm text-tp-text leading-relaxed">
                    Notre <strong>assistante virtuelle</strong> va vous appeler
                    {' '}<span className="whitespace-nowrap">sous 24h ouvrées</span>{' '}
                    pour fixer un rendez-vous avec le <strong>responsable de l'agence</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Étapes explicites */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-left">
              <Step
                icon={<Phone className="h-4 w-4" />}
                step="1"
                title="Appel de qualification"
                desc="Notre assistante valide votre besoin"
              />
              <Step
                icon={<Calendar className="h-4 w-4" />}
                step="2"
                title="Prise de RDV"
                desc="Choix d'un créneau qui vous arrange"
              />
              <Step
                icon={<Clock className="h-4 w-4" />}
                step="3"
                title="Rendez-vous"
                desc="Échange avec le responsable d'agence"
              />
            </div>

            {/* Contact alternatif */}
            <p className="mt-7 text-sm text-tp-text-light">
              Une question urgente&nbsp;?{' '}
              <a
                href="mailto:info@talent-partner-solutions.com"
                className="text-tp-accent font-medium hover:underline inline-flex items-center gap-1"
                data-testid="merci-mailto"
              >
                <Mail className="h-3.5 w-3.5" />
                info@talent-partner-solutions.com
              </a>
            </p>

            {/* CTA secondaire */}
            <div className="mt-7 pt-6 border-t border-tp-border">
              <Link to="/">
                <Button
                  variant="outline"
                  className="border-tp-primary text-tp-primary hover:bg-tp-primary hover:text-white"
                  data-testid="merci-cta-home"
                >
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </div>

          {/* Tag debug (utile pour Google Ads diagnostics) */}
          <div className="hidden" data-testid="merci-conversion-fired" data-from-form={fromForm} />
        </div>
      </main>

      {/* Footer minimal */}
      <footer className="bg-tp-primary text-white/70 text-center text-xs py-5 px-4">
        © {new Date().getFullYear()} Talent Partner Solutions — Tous droits réservés
      </footer>
    </div>
  );
};

const Step = ({ icon, step, title, desc }) => (
  <div className="bg-white border border-tp-border rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1.5">
      <span className="w-6 h-6 rounded-full bg-tp-accent text-white text-xs font-bold flex items-center justify-center shrink-0">
        {step}
      </span>
      <span className="text-tp-accent">{icon}</span>
    </div>
    <div className="font-semibold text-sm text-tp-primary leading-tight">{title}</div>
    <div className="text-xs text-tp-text-light mt-0.5">{desc}</div>
  </div>
);

export default MerciPage;
